import useRequest from "@/lib/request/useRequest";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
    faPlus,
    faSearch,
    faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Chip,
    Divider,
    Input,
    Select,
    SelectItem,
} from "@heroui/react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { User } from "@/types/user";
import TableV2 from "@/components/table/TableV2";

type NetworkUsersSearch = {
    search?: string;
    ordering?: string;
    organization?: string;
    account_type?: string;
    status?: string;
};

export const Route = createFileRoute("/dashboard/network/users")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): NetworkUsersSearch => {
        return {
            search: search.search as string,
            ordering: search.ordering as string,
            organization: search.organization as string,
            account_type: search.account_type as string,
            status: search.status as string,
        };
    },
});

const getOrgName = (org: User["organization"]): string =>
    typeof org === "string" ? org : (org?.name ?? "");

function formatAccountType(type?: string) {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
}

function RouteComponent() {
    const { useGetRequest } = useRequest();

    const { search, ordering, organization, account_type, status } =
        Route.useSearch();

    const navigate = Route.useNavigate();

    const usersQuery = useGetRequest<User[]>({
        url: "users/",
    });

    const organizationOptions = useMemo(() => {
        const names = (usersQuery.data ?? [])
            .map((u) => getOrgName(u.organization))
            .filter((name): name is string => !!name);

        return Array.from(new Set(names)).sort();
    }, [usersQuery.data]);

    const tableData = useMemo(() => {
        let data = usersQuery.data ?? [];

        if (search?.trim()) {
            const q = search.trim().toLowerCase();
            data = data.filter(
                (u) =>
                    u.full_name?.toLowerCase().includes(q) ||
                    u.username?.toLowerCase().includes(q) ||
                    getOrgName(u.organization).toLowerCase().includes(q),
            );
        }

        if (organization && organization !== "all") {
            data = data.filter(
                (u) => getOrgName(u.organization) === organization,
            );
        }

        if (account_type && account_type !== "all") {
            data = data.filter((u) => u.account_type === account_type);
        }

        if (status === "active" || !status) {
            data = data.filter((u) => u.is_active);
        }

        if (status === "inactive") {
            data = data.filter((u) => !u.is_active);
        }

        return data;
    }, [usersQuery.data, search, organization, account_type, status]);

    const columns: ColumnDef<User>[] = useMemo(
        () => [
            {
                id: "name",
                header: "Name",
                accessorFn: (row) => row.full_name,
                cell: (info) => (
                    <div className="text-sm font-medium px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 200,
            },
            {
                id: "email",
                header: "Email",
                accessorFn: (row) => row.username,
                cell: (info) => (
                    <div className="text-sm px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 300,
            },
            {
                id: "organization",
                header: "Organization",
                accessorFn: (row) => getOrgName(row.organization),
                cell: (info) => (
                    <div className="text-sm px-2">
                        {info.getValue() as string}
                    </div>
                ),
                size: 200,
            },
            {
                id: "account_type",
                header: "Type",
                accessorFn: (row) => row.account_type,
                cell: ({ row }) => {
                    const type = row.original.account_type;
                    const color =
                        type === "sentinel"
                            ? "primary"
                            : type === "consultant"
                              ? "secondary"
                              : "default";
                    return (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color={color}
                        >
                            {formatAccountType(type)}
                        </Chip>
                    );
                },
                size: 120,
            },
            {
                id: "status",
                header: "Status",
                accessorFn: (row) => row.is_active,
                cell: ({ row }) => (
                    <Chip
                        size="sm"
                        radius="sm"
                        variant="flat"
                        color={row.original.is_active ? "success" : "danger"}
                    >
                        {row.original.is_active ? "Active" : "Inactive"}
                    </Chip>
                ),
                size: 100,
            },
            {
                id: "edit",
                enableSorting: false,
                header: "",
                accessorFn: (row) => row.id,
                cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            variant="flat"
                            size="sm"
                            startContent={
                                <FontAwesomeIcon icon={faPenToSquare} />
                            }
                            onPress={() => {
                                navigate({
                                    to: "/dashboard/network/users/$userId",
                                    params: {
                                        userId: row.original.id.toString(),
                                    },
                                    search: (prev) => prev,
                                });
                            }}
                        >
                            Edit
                        </Button>
                    </div>
                ),
                size: 90,
            },
        ],
        [navigate],
    );

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: ordering
                ? ordering.startsWith("-")
                    ? ordering.slice(1)
                    : ordering
                : "name",
            desc: ordering ? ordering.startsWith("-") : false,
        },
    ]);

    useEffect(() => {
        if (sorting.length > 0) {
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${sorting[0].id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    return (
        <div className="h-full flex flex-col overflow-hidden px-4">
            <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                <div className="flex flex-col lg:flex-row gap-2 items-center">
                    <Input
                        size="sm"
                        placeholder="Search..."
                        value={search ?? ""}
                        onValueChange={(value) => {
                            navigate({
                                search: (prev) => ({ ...prev, search: value }),
                            });
                        }}
                        startContent={<FontAwesomeIcon icon={faSearch} />}
                        className="w-64"
                    />
                    <Autocomplete
                        size="sm"
                        aria-label="Organization"
                        placeholder="Organization"
                        selectedKey={
                            organization && organization !== "all"
                                ? organization
                                : null
                        }
                        onSelectionChange={(key) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    organization:
                                        key == null ? undefined : String(key),
                                }),
                            });
                        }}
                        className="w-56"
                    >
                        {organizationOptions.map((name) => (
                            <AutocompleteItem key={name} textValue={name}>
                                {name}
                            </AutocompleteItem>
                        ))}
                    </Autocomplete>
                    <Select
                        size="sm"
                        aria-label="Account Type"
                        selectedKeys={[account_type ?? "all"]}
                        onChange={(e) =>
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    account_type:
                                        e.target.value === "all"
                                            ? undefined
                                            : e.target.value,
                                }),
                            })
                        }
                        className="w-40"
                    >
                        <SelectItem key="all">All Types</SelectItem>
                        <SelectItem key="grower">Grower</SelectItem>
                        <SelectItem key="consultant">Consultant</SelectItem>
                        <SelectItem key="sentinel">Sentinel</SelectItem>
                    </Select>
                    <Select
                        size="sm"
                        aria-label="Status"
                        selectedKeys={[status ?? "active"]}
                        onChange={(e) =>
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    status:
                                        e.target.value === "active"
                                            ? undefined
                                            : e.target.value,
                                }),
                            })
                        }
                        className="w-40"
                    >
                        <SelectItem key="all">All Statuses</SelectItem>
                        <SelectItem key="active">Active</SelectItem>
                        <SelectItem key="inactive">Inactive</SelectItem>
                    </Select>
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Button
                        as={Link}
                        to="/dashboard/network/users/new"
                        color="primary"
                        size="sm"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Add User
                    </Button>
                </div>
            </div>
            <Divider />
            <TableV2
                data={tableData}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                isLoading={usersQuery.isLoading}
                showScrollbar={true}
                onRowClick={(row) =>
                    navigate({
                        to: "/dashboard/network/users/$userId",
                        params: { userId: row.id.toString() },
                        search: (prev) => prev,
                    })
                }
            />
            <Outlet />
        </div>
    );
}
